import React, { useEffect, useRef, useState } from "react";

import { useDisconnect } from "wagmi";
import useAuth from "../../hooks/useAuth";
import {
  selectGeneral,
  setDisconnectRedirectUrl,
  setShowExpireSessionPopup,
} from "../../redux/slices/generalSlice";
import { useDispatch, useSelector } from "react-redux";
import useOutsideClick from "../../hooks/useOutsideClick";
import useLocalStorage from "../../hooks/useLocalStorage";

// ExpireSessionPopup component to display a session expiration warning

const ExpireSessionPopup = () => {
  // State to track remaining seconds before session expiration
  const [seconds, setSeconds] = useState(10);

  // Reference for the setInterval function to manage the countdown
  const intervalRef = useRef(null);

  // Custom hooks for handling disconnection, authentication, and local storage
  const { disconnect } = useDisconnect();
  const { login } = useAuth();
  const { removeValue: removeLocalData } = useLocalStorage("token");

  // Countdown effect: decrement seconds every second
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (seconds > 0) setSeconds((prev) => prev - 1);
    }, 1000);

    // Cleanup: clear the interval when the component unmounts
    return () => clearInterval(intervalRef.current);
  }, []);

  // Effect to handle actions when seconds reach zero
  useEffect(() => {
    if (seconds <= 0) {
      // Dispatch action to set redirect URL on session expiration
      dispatch(setDisconnectRedirectUrl("/"));

      // Disconnect the wallet and remove local data
      disconnect();
      removeLocalData(["token", "role", "isSignMessageShowed"]);

      // Redirect the user to the home page
      window.location = "/";
    }
  }, [seconds]);

  // Redux dispatch and selector for managing the popup visibility
  const dispatch = useDispatch();
  const { showExpireSessionPopup: show } = useSelector(selectGeneral);

  // Function to close the popup
  const handleClose = () => {
    dispatch(setShowExpireSessionPopup(false));
  };

  // Reference for detecting clicks outside the popup to close it
  const modalRef = useRef(null);
  useOutsideClick(modalRef, handleClose, null, show);

  // Effect to control body overflow based on popup visibility
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  // JSX for the ExpireSessionPopup component
  return (
    <div className="min-w-[100vw] w-full h-screen flex justify-center items-center bg-[#241b4795] backdrop-blur-md fixed top-0 left-0  roboto-font z-50 ">
      <div className="  w-11/12 md:w-[450px] flex flex-col items-center py-7 rounded-3xl bg-white ">
        {/* Session expiration message */}
        <span className="text-2xl sm:text-3xl font-semibold abel-font mt-1 ">
          Your Session Expired!
        </span>
        {/* Countdown message */}
        <span className="text-[#464646] text-lg mt-4 text-center px-3">
          Your session has expired, and your wallet will be disconnected in{" "}
          <span className="mr-0.5 font-bold text-secondary">{seconds}</span>
          seconds
        </span>
        {/* Button to login again */}
        <button
          onClick={() => {
            dispatch(setShowExpireSessionPopup(false));
            // Login again with redirection after successful login
            login({
              loginAgain: true,
              redirectAfterLogin: "/",
            });
          }}
          className="w-10/12 md:w-8/12 mt-7 text-lg  text-white flex justify-center items-center bg-light-primary  py-3.5  rounded-[10px] "
        >
          Login Again
        </button>
        {/* ////////////////// */}
      </div>
    </div>
  );
};

export default ExpireSessionPopup;
